import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CreditCard, Download, Receipt } from 'lucide-react';
import { AppLayout } from '../../../components/layouts';
import PageHeader from '../../../components/dashboard/PageHeader';
import UserService from '../../../services/user.service';
import { modalField, modalSecondaryBtn } from '../../../components/ui/modalDesign';

type PaymentRow = {
    programId: number | string;
    trxId: string;
    paymentDate: string;
    childName: string;
    packageTitle: string;
    packageName: string;
    packageLevel: number | string;
    cohortTitle: string;
};

const toDateOnly = (value?: string) => {
    if (!value) return '';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toISOString().slice(0, 10);
};

const isStripeSessionId = (trxId?: string) => {
    if (!trxId) return false;
    return trxId.startsWith('cs_test_') || trxId.startsWith('cs_live_');
};

export default function PaymentHistory() {
    const userService = new UserService();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [downloadingTrx, setDownloadingTrx] = useState<string>('');
    const [rows, setRows] = useState<PaymentRow[]>([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    useEffect(() => {
        const getHistory = async () => {
            setLoading(true);
            try {
                const response = await userService.getChildren();
                if (!response?.status || !Array.isArray(response?.data)) {
                    setRows([]);
                    return;
                }

                const mapped: PaymentRow[] = response.data.flatMap((child: any) => {
                    if (!Array.isArray(child?.programs)) return [];
                    return child.programs
                        .filter((program: any) => Boolean(program?.isPaid) && program?.trxId)
                        .map((program: any) => ({
                            programId: program?.id,
                            trxId: String(program?.trxId || ''),
                            paymentDate: String(program?.updatedAt || ''),
                            childName: `${child?.firstName || ''} ${child?.lastName || ''}`.trim(),
                            packageTitle: program?.package?.title || 'N/A',
                            packageName: program?.package?.name || 'N/A',
                            packageLevel: program?.package?.level ?? 'N/A',
                            cohortTitle: program?.cohort?.title || 'N/A',
                        }));
                });

                mapped.sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime());
                setRows(mapped);
            } catch (err: any) {
                toast.error(err?.message || 'Failed to fetch payment history');
            } finally {
                setLoading(false);
            }
        };

        getHistory();
    }, []);

    const filteredRows = useMemo(() => {
        return rows.filter((row) => {
            const day = toDateOnly(row.paymentDate);
            if (!day) return false;
            if (fromDate && day < fromDate) return false;
            if (toDate && day > toDate) return false;
            return true;
        });
    }, [rows, fromDate, toDate]);

    const downloadStripeReceipt = async (trxId: string) => {
        if (!trxId) return;
        if (!isStripeSessionId(trxId)) return;
        try {
            setDownloadingTrx(trxId);
            const response = await userService.getStripeReceiptLinkByTrxId(trxId);
            const url = response?.data?.receiptUrl;
            if (!response?.status || !url) {
                toast.error(response?.message || 'Receipt link not found');
                return;
            }
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (err: any) {
            toast.error(err?.message || 'Unable to download receipt');
        } finally {
            setDownloadingTrx('');
        }
    };

    return (
        <AppLayout>
            <PageHeader
                title="Payment History"
                subtitle="View completed payments and open Stripe receipts."
            />
            <div className="mb-4 flex justify-end">
                <button
                    type="button"
                    className="rounded-[10px] border border-[#E8E0EC] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#476788] transition hover:border-ryd-primary hover:text-ryd-primary"
                    onClick={() => navigate('/parent/home')}
                >
                    Back to Home
                </button>
            </div>

            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-[18px] border border-[#efe6f1] bg-white p-4 shadow-[0_8px_30px_rgba(136,22,122,0.04)] sm:p-5">
                    <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-ryd-primaryLess1 text-ryd-primary">
                        <CreditCard size={18} />
                    </div>
                    <p className="text-[12px] font-medium uppercase tracking-wide text-[#94a3b8]">Total Transactions</p>
                    <p className="mt-1 font-[AvertaStd-Semibold] text-[26px] text-ryd-headerTextPrimary">{filteredRows.length}</p>
                </div>
                <div className="rounded-[18px] border border-[#efe6f1] bg-white p-4 shadow-[0_8px_30px_rgba(136,22,122,0.04)] sm:p-5">
                    <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-ryd-primaryLess1 text-ryd-primary">
                        <Receipt size={18} />
                    </div>
                    <p className="text-[12px] font-medium uppercase tracking-wide text-[#94a3b8]">Latest Payment</p>
                    <p className="mt-1 font-[AvertaStd-Semibold] text-[16px] text-ryd-headerTextPrimary">
                        {filteredRows[0]?.paymentDate ? new Date(filteredRows[0].paymentDate).toLocaleDateString() : 'N/A'}
                    </p>
                </div>
            </div>

            <div className="mb-4 flex flex-col gap-3 rounded-[18px] border border-[#efe6f1] bg-white p-4 shadow-sm sm:flex-row sm:items-end">
                <div className="min-w-0 flex-1">
                    <label className="mb-1.5 block text-[13px] font-semibold text-ryd-headerTextPrimary">From date</label>
                    <input type="date" className={modalField} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </div>
                <div className="min-w-0 flex-1">
                    <label className="mb-1.5 block text-[13px] font-semibold text-ryd-headerTextPrimary">To date</label>
                    <input type="date" className={modalField} value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </div>
                <button
                    type="button"
                    className={`${modalSecondaryBtn} sm:w-auto`}
                    onClick={() => {
                        setFromDate('');
                        setToDate('');
                    }}
                >
                    Clear filter
                </button>
            </div>

            <div className="overflow-hidden rounded-[18px] border border-[#efe6f1] bg-white shadow-[0_8px_30px_rgba(136,22,122,0.04)]">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] text-left">
                        <thead>
                            <tr className="border-b border-[#efe6f1] bg-[#FBF8FC] text-[12px] font-semibold uppercase tracking-wide text-[#8b9aab]">
                                <th className="px-4 py-3">Payment Date</th>
                                <th className="px-4 py-3">Child</th>
                                <th className="px-4 py-3">Package</th>
                                <th className="px-4 py-3">Level</th>
                                <th className="px-4 py-3">Cohort</th>
                                <th className="px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={6} className="px-4 py-10 text-center text-[14px] text-[#8b9aab]">
                                        Loading payment history…
                                    </td>
                                </tr>
                            )}
                            {!loading && filteredRows.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-4 py-10 text-center text-[14px] text-[#8b9aab]">
                                        No payment found for selected filters.
                                    </td>
                                </tr>
                            )}
                            {!loading &&
                                filteredRows.map((row) => {
                                    const canDownloadStripeReceipt = isStripeSessionId(row.trxId);
                                    const isDownloading = downloadingTrx === row.trxId;
                                    return (
                                        <tr key={`${row.programId}-${row.trxId}`} className="border-b border-[#f3ebf3] text-[13px] text-[#334155]">
                                            <td className="px-4 py-3">{new Date(row.paymentDate).toLocaleString()}</td>
                                            <td className="px-4 py-3">{row.childName}</td>
                                            <td className="px-4 py-3">
                                                {row.packageTitle} ({row.packageName})
                                            </td>
                                            <td className="px-4 py-3">{row.packageLevel}</td>
                                            <td className="px-4 py-3">{row.cohortTitle}</td>
                                            <td className="px-4 py-3">
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center gap-1.5 rounded-[10px] border border-ryd-primary/20 bg-ryd-primaryLess1 px-3 py-1.5 text-[12px] font-semibold text-ryd-primary transition hover:bg-ryd-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                                    disabled={!canDownloadStripeReceipt || isDownloading}
                                                    onClick={() => downloadStripeReceipt(row.trxId)}
                                                    title={!canDownloadStripeReceipt ? 'Receipt is only available for Stripe transactions' : ''}
                                                >
                                                    <Download size={13} />
                                                    {isDownloading ? 'Opening…' : 'Download Receipt'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
            </div>
        </AppLayout>
    );
}
